/**
 * Properties panel — right side, shows the selected feature.
 * Name, status, suppress toggle, delete; extrude gets distance/op fields,
 * sketch gets plane info + Edit button. All edits go through App/doc.apply
 * so they land in undo history with labels.
 */

import type { App } from '../app';
import { evaluateExpr, isExpr, type Expr } from '@core/expr';
import { constraintLabel } from '@core/geometry/constraints';
import type {
  ExtrudeParams,
  GroupParams,
  InstanceParams,
  MirrorParams,
  NumberOrExpr,
  RevolveParams,
  ScaleParams,
  SketchParams,
  TransformParams,
} from '@core/timeline/types';

export function mountPropertiesPanel(host: HTMLElement, app: App): void {
  const panel = document.createElement('div');
  panel.className = 'nv-props';
  host.appendChild(panel);

  const render = (): void => {
    const id = app.selected;
    const feature = id ? app.doc.timeline.get(id) : undefined;
    if (!feature || app.sketch.active) {
      panel.style.display = 'none';
      return;
    }
    panel.style.display = 'block';
    panel.innerHTML = '';

    const h = document.createElement('h3');
    h.textContent = feature.type;
    panel.appendChild(h);

    const row = (label: string, control: HTMLElement): void => {
      const r = document.createElement('div');
      r.className = 'row';
      const l = document.createElement('label');
      l.textContent = label;
      r.append(l, control);
      panel.appendChild(r);
    };

    // Name
    const name = document.createElement('input');
    name.type = 'text';
    name.value = feature.name;
    name.addEventListener('change', () => void app.renameSelected(name.value));
    row('Name', name);

    /**
     * Numeric field that also accepts parameter expressions: "width/2"
     * stores {$expr:"width/2"} (validated against the parameter table
     * first) and shows the computed value in the tooltip. Fields whose
     * stored unit differs from what's typed (angles) pass expr=false.
     */
    const numberField = (
      label: string,
      value: NumberOrExpr,
      onCommit: (v: NumberOrExpr) => void,
      step = 0.5,
      expr = true,
    ): void => {
      const input = document.createElement('input');
      const isE = isExpr(value);
      if (isE || expr) {
        input.type = 'text';
      } else {
        input.type = 'number';
        input.step = String(step);
      }
      if (isE) {
        input.value = value.$expr;
        input.classList.add('has-expr');
        try {
          input.title = `= ${Math.round(evaluateExpr(value.$expr, app.paramScope) * 1000) / 1000}`;
        } catch (err) {
          input.classList.add('expr-error');
          input.title = err instanceof Error ? err.message : String(err);
        }
      } else {
        input.value = String(Math.round((value as number) * 1000) / 1000);
        if (expr) input.title = 'Number or expression (e.g. width/2)';
      }
      input.addEventListener('change', () => {
        const raw = input.value.trim();
        const v = Number(raw);
        if (raw !== '' && Number.isFinite(v)) {
          onCommit(v);
          return;
        }
        if (!expr || raw === '') return;
        try {
          evaluateExpr(raw, app.paramScope);
        } catch (err) {
          app.setIoStatus(
            `Expression error: ${err instanceof Error ? err.message : String(err)}`,
          );
          return;
        }
        onCommit({ $expr: raw });
      });
      row(label, input);
      // One-shot focus request (e.g. Scale just added → type a factor now).
      if (app.focusPropertyField === label) {
        app.focusPropertyField = null;
        setTimeout(() => {
          input.focus();
          input.select();
        }, 0);
      }
    };

    /** Resolved number for guards on values that may be expressions. */
    const asNumber = (v: NumberOrExpr): number | null => {
      if (typeof v === 'number') return v;
      try {
        return evaluateExpr((v as Expr).$expr, app.paramScope);
      } catch {
        return null;
      }
    };
    void asNumber;

    const opSelect = (current: string): void => {
      const op = document.createElement('select');
      for (const [value, label] of [
        ['new-body', 'New body'],
        ['join', 'Join'],
        ['cut', 'Cut'],
        ['intersect', 'Intersect'],
      ] as const) {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = label;
        op.appendChild(o);
      }
      op.value = current;
      op.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { op: op.value }),
      );
      row('Operation', op);
    };

    if (feature.type === 'extrude') {
      const params = feature.params as ExtrudeParams;
      numberField('Distance', params.distance, (v) => {
        if (typeof v === 'number' && Math.abs(v) < 0.5) return;
        void app.updateFeatureParams(feature.id, { distance: v });
      });
      numberField(
        'End scale ×',
        params.endScale ?? 1,
        (v) => {
          if (typeof v === 'number' && v < 0) return;
          void app.updateFeatureParams(feature.id, { endScale: v });
        },
        0.05,
      );
      opSelect(params.op);
    }

    if (feature.type === 'revolve') {
      const params = feature.params as RevolveParams;
      numberField(
        'Angle °',
        (params.angle * 180) / Math.PI,
        (v) => {
          if (typeof v === 'number' && v > 0.1 && v <= 360) {
            void app.updateFeatureParams(feature.id, { angle: (v * Math.PI) / 180 });
          }
        },
        5,
        false, // stored in radians — a raw expression would be misread
      );
      numberField('Axis X', params.axisOrigin[0], (v) =>
        void app.updateFeatureParams(feature.id, { axisOrigin: [v, params.axisOrigin[1]] }),
      );
      numberField('Axis Y', params.axisOrigin[1], (v) =>
        void app.updateFeatureParams(feature.id, { axisOrigin: [params.axisOrigin[0], v] }),
      );
      const dir = document.createElement('select');
      for (const [value, label] of [
        ['0,1', 'Sketch Y axis'],
        ['1,0', 'Sketch X axis'],
      ] as const) {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = label;
        dir.appendChild(o);
      }
      dir.value = Math.abs(params.axisDirection[0]) > Math.abs(params.axisDirection[1]) ? '1,0' : '0,1';
      dir.addEventListener('change', () => {
        const [x, y] = dir.value.split(',').map(Number);
        void app.updateFeatureParams(feature.id, { axisDirection: [x, y] });
      });
      row('Axis dir', dir);
      opSelect(params.op);
    }

    if (feature.type === 'sweep') {
      opSelect((feature.params as { op: string }).op);
    }

    if (feature.type === 'loft') {
      const params = feature.params as import('@core/timeline/types').LoftParams;
      const profiles = document.createElement('span');
      profiles.textContent = params.profiles
        .map((p) => app.doc.timeline.get(p.$feature)?.name ?? '?')
        .join(' → ');
      profiles.title = 'Stations, in loft order';
      row('Profiles', profiles);

      const ruled = document.createElement('input');
      ruled.type = 'checkbox';
      ruled.checked = !!params.ruled;
      ruled.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { ruled: ruled.checked || undefined }),
      );
      row('Ruled (straight)', ruled);

      // Spine guide: any sketch with an open curve, earlier in the timeline.
      const spineSel = document.createElement('select');
      const none = document.createElement('option');
      none.value = '';
      none.textContent = 'none (plain loft)';
      spineSel.appendChild(none);
      const myPos = app.doc.timeline.positionOf(feature.id);
      for (const f of app.doc.timeline.features) {
        if (f.type !== 'sketch' || app.doc.timeline.positionOf(f.id) >= myPos) continue;
        if (params.profiles.some((p) => p.$feature === f.id)) continue;
        const o = document.createElement('option');
        o.value = f.id;
        o.textContent = f.name;
        spineSel.appendChild(o);
      }
      spineSel.value = params.spine?.$feature ?? '';
      spineSel.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, {
          spine: spineSel.value ? { $feature: spineSel.value } : undefined,
        }),
      );
      row('Spine guide', spineSel);
      opSelect(params.op);
    }

    if (feature.type === 'scale') {
      const params = feature.params as ScaleParams;
      const uniform = params.factor ?? 1;
      const factors = params.factors ?? [uniform, uniform, uniform];
      (['Factor X', 'Factor Y', 'Factor Z'] as const).forEach((label, axis) => {
        numberField(
          label,
          factors[axis]!,
          (v) => {
            if (typeof v === 'number' && v <= 0) return;
            const next = [...factors] as unknown[];
            next[axis] = v;
            void app.updateFeatureParams(feature.id, { factors: next });
          },
          0.1,
        );
      });
      const target = document.createElement('span');
      target.textContent = app.doc.timeline.get(params.target.$feature)?.name ?? '?';
      row('Target', target);
    }

    if (feature.type === 'transform' || feature.type === 'instance') {
      const params = feature.params as TransformParams;
      (['Move X', 'Move Y', 'Move Z'] as const).forEach((label, axis) => {
        numberField(label, params.translation[axis]!, (v) => {
          const next = [...params.translation] as unknown[];
          next[axis] = v;
          void app.updateFeatureParams(feature.id, { translation: next });
        });
      });
      const axisSel = document.createElement('select');
      for (const a of ['X', 'Y', 'Z'] as const) {
        const o = document.createElement('option');
        o.value = a;
        o.textContent = `About ${a}`;
        axisSel.appendChild(o);
      }
      axisSel.value = params.rotationAxis;
      axisSel.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { rotationAxis: axisSel.value }),
      );
      row('Rotate axis', axisSel);
      numberField(
        'Angle °',
        (params.rotationAngle * 180) / Math.PI,
        (v) => {
          if (typeof v !== 'number') return;
          void app.updateFeatureParams(feature.id, { rotationAngle: (v * Math.PI) / 180 });
        },
        5,
        false,
      );
      numberField(
        'Copies',
        params.copies,
        (v) => {
          if (typeof v === 'number' && v >= 0 && Number.isInteger(v)) {
            void app.updateFeatureParams(feature.id, { copies: v });
          }
        },
        1,
        false,
      );
    }

    if (feature.type === 'group') {
      const params = feature.params as GroupParams;
      const members = document.createElement('span');
      members.textContent = params.targets
        .map((t) => app.doc.timeline.get(t.$feature)?.name ?? '?')
        .join(', ');
      members.title = 'Delete this group to ungroup — the bodies reappear';
      row('Members', members);
    }

    if (feature.type === 'hole') {
      const params = feature.params as import('@core/timeline/types').HoleParams;
      const count = document.createElement('span');
      count.textContent = String(params.holes.length);
      row('Positions', count);

      // Standard clearance sizes (normal fit).
      const size = document.createElement('select');
      const SIZES: [string, number, number][] = [
        ['M3', 3.4, 6.5], ['M4', 4.5, 8], ['M5', 5.5, 10],
        ['M6', 6.6, 11], ['M8', 9, 15], ['custom', 0, 0],
      ];
      for (const [label] of SIZES) {
        const o = document.createElement('option');
        o.value = label;
        o.textContent = label;
        size.appendChild(o);
      }
      const current = SIZES.find(([, d]) => d === params.diameter)?.[0] ?? 'custom';
      size.value = current;
      size.addEventListener('change', () => {
        const s = SIZES.find(([l]) => l === size.value);
        if (s && s[1] > 0) {
          void app.updateFeatureParams(feature.id, {
            diameter: s[1],
            ...(params.style === 'counterbore' ? { cbDiameter: s[2] } : {}),
            ...(params.style === 'countersink' ? { csDiameter: s[2] } : {}),
          });
        }
      });
      row('Size', size);

      numberField('Diameter', params.diameter, (v) => {
        if (typeof v === 'number' && v <= 0) return;
        void app.updateFeatureParams(feature.id, { diameter: v });
      });

      const styleSel = document.createElement('select');
      for (const s of ['plain', 'counterbore', 'countersink']) {
        const o = document.createElement('option');
        o.value = s;
        o.textContent = s;
        styleSel.appendChild(o);
      }
      styleSel.value = params.style;
      styleSel.addEventListener('change', () => {
        const patch: Record<string, unknown> = { style: styleSel.value };
        if (styleSel.value === 'counterbore' && params.cbDiameter === undefined) {
          patch.cbDiameter = (typeof params.diameter === 'number' ? params.diameter : 5) * 1.8;
          patch.cbDepth = 3;
        }
        if (styleSel.value === 'countersink' && params.csDiameter === undefined) {
          patch.csDiameter = (typeof params.diameter === 'number' ? params.diameter : 5) * 2;
        }
        void app.updateFeatureParams(feature.id, patch);
      });
      row('Style', styleSel);

      const through = document.createElement('input');
      through.type = 'checkbox';
      through.checked = params.depth === 'through';
      through.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { depth: through.checked ? 'through' : 10 }),
      );
      row('Through', through);
      if (params.depth !== 'through') {
        numberField('Depth', params.depth, (v) => {
          if (typeof v === 'number' && v <= 0) return;
          void app.updateFeatureParams(feature.id, { depth: v });
        });
      }
      if (params.style === 'counterbore') {
        numberField('CB diameter', params.cbDiameter ?? 9, (v) =>
          void app.updateFeatureParams(feature.id, { cbDiameter: v }));
        numberField('CB depth', params.cbDepth ?? 3, (v) =>
          void app.updateFeatureParams(feature.id, { cbDepth: v }));
      }
      if (params.style === 'countersink') {
        numberField('CS diameter', params.csDiameter ?? 10, (v) =>
          void app.updateFeatureParams(feature.id, { csDiameter: v }));
        numberField('CS angle °', params.csAngle ?? 90, (v) => {
          if (typeof v === 'number' && v > 10 && v < 180) {
            void app.updateFeatureParams(feature.id, { csAngle: v });
          }
        }, 1, false);
      }
    }

    if (feature.type === 'pattern') {
      const params = feature.params as import('@core/timeline/types').PatternParams;
      const kindSel = document.createElement('select');
      for (const k of ['linear', 'circular']) {
        const o = document.createElement('option');
        o.value = k;
        o.textContent = k;
        kindSel.appendChild(o);
      }
      kindSel.value = params.kind;
      kindSel.addEventListener('change', () => {
        const patch: Record<string, unknown> = { kind: kindSel.value };
        if (kindSel.value === 'circular' && !params.center) {
          patch.axis = 'Z';
          patch.center = [0, 0, 0];
        }
        if (kindSel.value === 'linear' && !params.step) patch.step = [20, 0, 0];
        void app.updateFeatureParams(feature.id, patch);
      });
      row('Kind', kindSel);
      numberField('Count', params.count, (v) => {
        if (typeof v === 'number' && (v < 2 || !Number.isInteger(v))) return;
        void app.updateFeatureParams(feature.id, { count: v });
      }, 1);
      if (params.kind === 'linear') {
        const step = params.step ?? [20, 0, 0];
        (['Step X', 'Step Y', 'Step Z'] as const).forEach((label, axis) => {
          numberField(label, step[axis]!, (v) => {
            const next = [...step] as unknown[];
            next[axis] = v;
            void app.updateFeatureParams(feature.id, { step: next });
          });
        });
      } else {
        const axisSel = document.createElement('select');
        for (const a of ['X', 'Y', 'Z']) {
          const o = document.createElement('option');
          o.value = a;
          o.textContent = `About ${a}`;
          axisSel.appendChild(o);
        }
        axisSel.value = params.axis ?? 'Z';
        axisSel.addEventListener('change', () =>
          void app.updateFeatureParams(feature.id, { axis: axisSel.value }),
        );
        row('Axis', axisSel);
        const center = params.center ?? [0, 0, 0];
        (['Center X', 'Center Y', 'Center Z'] as const).forEach((label, axis) => {
          numberField(label, center[axis]!, (v) => {
            const next = [...center] as unknown[];
            next[axis] = v;
            void app.updateFeatureParams(feature.id, { center: next });
          });
        });
        numberField('Total angle °', params.totalAngleDeg ?? 360, (v) => {
          if (typeof v === 'number' && (v <= 0 || v > 360)) return;
          void app.updateFeatureParams(feature.id, { totalAngleDeg: v });
        }, 5);
      }
    }

    if (feature.type === 'primitive') {
      const params = feature.params as import('@core/timeline/types').PrimitiveParams;
      const shape = document.createElement('span');
      shape.textContent = params.shape;
      row('Shape', shape);
      const SIZE_LABELS: [keyof typeof params & string, string][] = [
        ['dx', 'Size X'], ['dy', 'Size Y'], ['dz', 'Size Z'],
        ['radius', 'Radius'], ['radius2', 'Top radius'], ['height', 'Height'],
        ['majorRadius', 'Ring radius'], ['minorRadius', 'Tube radius'],
      ];
      for (const [key, label] of SIZE_LABELS) {
        const value = (params as unknown as Record<string, NumberOrExpr | undefined>)[key];
        if (value === undefined) continue;
        numberField(label, value, (v) => {
          if (typeof v === 'number' && v < 0) return;
          void app.updateFeatureParams(feature.id, { [key]: v });
        });
      }
      (['At X', 'At Y', 'At Z'] as const).forEach((label, axis) => {
        numberField(label, params.at[axis]!, (v) => {
          if (typeof v !== 'number') return;
          const next = [...params.at] as number[];
          next[axis] = v;
          void app.updateFeatureParams(feature.id, { at: next });
        });
      });
      if (params.shape !== 'box' && params.shape !== 'sphere') {
        const axisSel = document.createElement('select');
        for (const a of ['X', 'Y', 'Z']) {
          const o = document.createElement('option');
          o.value = a;
          o.textContent = `Along ${a}`;
          axisSel.appendChild(o);
        }
        axisSel.value = params.axis;
        axisSel.addEventListener('change', () =>
          void app.updateFeatureParams(feature.id, { axis: axisSel.value }),
        );
        row('Axis', axisSel);
      }
      opSelect(params.op);
    }

    if (feature.type === 'press-pull') {
      const params = feature.params as import('@core/timeline/types').PressPullParams;
      numberField('Offset', params.offset, (v) => {
        if (typeof v === 'number' && Math.abs(v) < 0.001) return;
        void app.updateFeatureParams(feature.id, { offset: v });
      });
    }

    if (feature.type === 'draft') {
      const params = feature.params as import('@core/timeline/types').DraftParams;
      numberField('Angle °', params.angleDeg, (v) =>
        void app.updateFeatureParams(feature.id, { angleDeg: v }));
      numberField('Neutral @', params.neutralOffset, (v) =>
        void app.updateFeatureParams(feature.id, { neutralOffset: v }));
      const axisSel = document.createElement('select');
      for (const a of ['X', 'Y', 'Z']) {
        const o = document.createElement('option');
        o.value = a; o.textContent = `Pull ${a}`;
        axisSel.appendChild(o);
      }
      axisSel.value = params.axis;
      axisSel.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { axis: axisSel.value }));
      row('Axis', axisSel);
      const facesInfo = document.createElement('span');
      facesInfo.textContent = params.faces?.length ? `${params.faces.length} picked` : 'all walls';
      row('Faces', facesInfo);
    }

    if (feature.type === 'thread') {
      const params = feature.params as import('@core/timeline/types').ThreadParams;
      numberField('Diameter', params.diameter, (v) => {
        if (typeof v === 'number' && v <= 0) return;
        void app.updateFeatureParams(feature.id, { diameter: v });
      });
      numberField('Pitch', params.pitch, (v) => {
        if (typeof v === 'number' && v <= 0) return;
        void app.updateFeatureParams(feature.id, { pitch: v });
      });
      numberField('Length', params.length, (v) => {
        if (typeof v === 'number' && v <= 0) return;
        void app.updateFeatureParams(feature.id, { length: v });
      });
      const axisSel = document.createElement('select');
      for (const a of ['X', 'Y', 'Z']) {
        const o = document.createElement('option');
        o.value = a; o.textContent = `Along ${a}`;
        axisSel.appendChild(o);
      }
      axisSel.value = params.axis;
      axisSel.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { axis: axisSel.value }));
      row('Axis', axisSel);
      opSelect(params.op);
    }

    if (feature.type === 'coil') {
      const params = feature.params as import('@core/timeline/types').CoilParams;
      numberField('Radius', params.radius, (v) => {
        if (typeof v === 'number' && v <= 0) return;
        void app.updateFeatureParams(feature.id, { radius: v });
      });
      numberField('Pitch', params.pitch, (v) => {
        if (typeof v === 'number' && v <= 0) return;
        void app.updateFeatureParams(feature.id, { pitch: v });
      });
      numberField('Turns', params.turns, (v) => {
        if (typeof v === 'number' && v <= 0) return;
        void app.updateFeatureParams(feature.id, { turns: v });
      }, 0.5);
      numberField('Wire ⌀', params.wireDiameter, (v) => {
        if (typeof v === 'number' && v <= 0) return;
        void app.updateFeatureParams(feature.id, { wireDiameter: v });
      });
      const axisSel = document.createElement('select');
      for (const a of ['X', 'Y', 'Z']) {
        const o = document.createElement('option');
        o.value = a;
        o.textContent = `Along ${a}`;
        axisSel.appendChild(o);
      }
      axisSel.value = params.axis;
      axisSel.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { axis: axisSel.value }),
      );
      row('Axis', axisSel);
      (['Origin X', 'Origin Y', 'Origin Z'] as const).forEach((label, axis) => {
        numberField(label, params.axisOrigin[axis]!, (v) => {
          if (typeof v !== 'number') return;
          const next = [...params.axisOrigin] as number[];
          next[axis] = v;
          void app.updateFeatureParams(feature.id, { axisOrigin: next });
        });
      });
      const left = document.createElement('input');
      left.type = 'checkbox';
      left.checked = !!params.leftHanded;
      left.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { leftHanded: left.checked || undefined }),
      );
      row('Left-handed', left);
      opSelect(params.op);
    }

    if (feature.type === 'mate') {
      const params = feature.params as import('@core/timeline/types').MateParams;
      const who = (r: { $feature: string }): string =>
        app.doc.timeline.get(r.$feature as never)?.name ?? '?';
      const pair = document.createElement('span');
      pair.textContent = `${who(params.target)} → ${who(params.to)}`;
      pair.title = 'First body moves; second stays';
      row('Bodies', pair);
      const anchorSel = (
        label: string,
        value: string,
        key: 'anchorTarget' | 'anchorTo',
      ): void => {
        const sel = document.createElement('select');
        for (const a of ['center', 'bottom', 'top', 'min', 'max']) {
          const o = document.createElement('option');
          o.value = a;
          o.textContent = a;
          sel.appendChild(o);
        }
        sel.value = value;
        sel.addEventListener('change', () =>
          void app.updateFeatureParams(feature.id, { [key]: sel.value }),
        );
        row(label, sel);
      };
      const kind = params.kind ?? 'anchor';
      const kindLabel = document.createElement('span');
      kindLabel.textContent = kind;
      row('Type', kindLabel);
      if (kind === 'anchor') {
        anchorSel('Move anchor', params.anchorTarget, 'anchorTarget');
        anchorSel('Onto anchor', params.anchorTo, 'anchorTo');
        (['Offset X', 'Offset Y', 'Offset Z'] as const).forEach((label, axis) => {
          numberField(label, params.offset[axis]!, (v) => {
            const next = [...params.offset] as unknown[];
            next[axis] = v;
            void app.updateFeatureParams(feature.id, { offset: next });
          });
        });
      } else if (kind === 'distance') {
        numberField('Gap', params.gap ?? 0, (v) =>
          void app.updateFeatureParams(feature.id, { gap: v }),
        );
      } else if (kind === 'angle') {
        numberField('Angle °', params.angleDeg ?? 0, (v) =>
          void app.updateFeatureParams(feature.id, { angleDeg: v }), 1, false);
      }
    }

    if (feature.type === 'part') {
      const params = feature.params as import('@core/timeline/types').PartParams;
      const src = document.createElement('button');
      src.textContent = `${params.source} ↗`;
      src.title = 'Open the source project — edits there update this assembly';
      src.style.cssText =
        'border:1px solid var(--panel-border);background:#20242b;color:var(--text);' +
        'border-radius:5px;font:inherit;font-size:11px;padding:2px 8px;cursor:pointer;';
      src.addEventListener('click', () => void app.switchProject(params.projectId));
      row('Source', src);
      (['Pos X', 'Pos Y', 'Pos Z'] as const).forEach((label, axis) => {
        numberField(label, params.position[axis]!, (v) => {
          const next = [...params.position] as unknown[];
          next[axis] = v;
          void app.updateFeatureParams(feature.id, { position: next });
        });
      });
      (['Rot X °', 'Rot Y °', 'Rot Z °'] as const).forEach((label, axis) => {
        numberField(
          label,
          (params.rotationEuler[axis]! * 180) / Math.PI,
          (v) => {
            if (typeof v !== 'number') return;
            const next = [...params.rotationEuler] as number[];
            next[axis] = (v * Math.PI) / 180;
            void app.updateFeatureParams(feature.id, { rotationEuler: next });
          },
          5,
          false,
        );
      });
    }

    if (feature.type === 'instance') {
      const params = feature.params as unknown as InstanceParams;
      const src = document.createElement('span');
      src.textContent = app.doc.timeline.get(params.source.$feature)?.name ?? '?';
      src.title = 'Component source — editing it updates every instance';
      row('Source', src);
    }

    if (feature.type === 'fillet' || feature.type === 'chamfer') {
      const p = feature.params as { radius?: number; distance?: number; edges?: unknown[] };
      numberField(
        feature.type === 'fillet' ? 'Radius' : 'Distance',
        (feature.type === 'fillet' ? p.radius : p.distance)!,
        (v) => {
          if (typeof v === 'number' && v <= 0) return;
          void app.updateFeatureParams(
            feature.id,
            feature.type === 'fillet' ? { radius: v } : { distance: v },
          );
        },
        0.5,
      );
      const edges = document.createElement('span');
      edges.textContent = p.edges?.length ? `${p.edges.length} picked` : 'all';
      row('Edges', edges);
      if (p.edges?.length) {
        const reset = document.createElement('button');
        reset.className = 'chat-mini';
        reset.textContent = 'Reset to all edges';
        reset.style.marginTop = '4px';
        reset.addEventListener('click', () =>
          void app.updateFeatureParams(feature.id, { edges: undefined }),
        );
        panel.appendChild(reset);
      }
    }
    if (feature.type === 'shell') {
      const params = feature.params as { thickness: number; open: string };
      numberField('Thickness', params.thickness, (v) => {
        if (typeof v === 'number' && v <= 0) return;
        void app.updateFeatureParams(feature.id, { thickness: v });
      }, 0.5);
      const openSel = document.createElement('select');
      for (const [value, label] of [
        ['top', 'Open top'],
        ['bottom', 'Open bottom'],
        ['none', 'Closed (hollow)'],
      ] as const) {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = label;
        openSel.appendChild(o);
      }
      openSel.value = params.open;
      openSel.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { open: openSel.value }),
      );
      row('Opening', openSel);
    }

    if (feature.type === 'mirror') {
      const params = feature.params as MirrorParams;
      const plane = document.createElement('select');
      for (const [value, label] of [
        ['YZ', 'YZ (reflect X)'],
        ['XZ', 'XZ (reflect Y)'],
        ['XY', 'XY (reflect Z)'],
      ] as const) {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = label;
        plane.appendChild(o);
      }
      plane.value = params.plane;
      plane.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { plane: plane.value }),
      );
      row('Plane', plane);
      numberField('Offset', params.offset, (v) =>
        void app.updateFeatureParams(feature.id, { offset: v }),
      );
      const keep = document.createElement('input');
      keep.type = 'checkbox';
      keep.checked = params.keepOriginal;
      keep.addEventListener('change', () =>
        void app.updateFeatureParams(feature.id, { keepOriginal: keep.checked }),
      );
      row('Keep original', keep);
    }

    if (feature.type === 'sketch') {
      const params = feature.params as SketchParams;
      const planeInfo = document.createElement('span');
      if (params.plane.kind === 'datum') {
        planeInfo.textContent = `${params.plane.plane} @ ${params.plane.offset}`;
      } else if (params.plane.kind === 'face') {
        const parent = app.doc.timeline.get(params.plane.face.$feature);
        planeInfo.textContent = `${parent?.name ?? '?'} · ${params.plane.face.path}`;
        planeInfo.title = 'Face-attached: this sketch rides the face through edits';
      } else if (params.plane.kind === 'bface') {
        const parent = app.doc.timeline.get(params.plane.body.$feature);
        planeInfo.textContent = `face of ${parent?.name ?? '?'}`;
        planeInfo.title = 'Attached to a planar B-rep face — rides it through edits';
      } else {
        const n = params.plane.normal;
        planeInfo.textContent = `free · n(${n.map((v) => Math.round(v * 100) / 100).join(', ')})`;
        planeInfo.title = 'Free plane captured from a face pick';
      }
      row('Plane', planeInfo);
      const count = document.createElement('span');
      count.textContent = String(params.entities.length);
      row('Entities', count);

      // Constraint list: label + remove. Values that are expressions show
      // as-is; deleting re-solves (fewer constraints always still solve).
      const constraints = params.constraints ?? [];
      if (constraints.length > 0) {
        const h4 = document.createElement('h3');
        h4.textContent = 'Constraints';
        h4.style.marginTop = '10px';
        panel.appendChild(h4);
        constraints.forEach((c, i) => {
          const crow = document.createElement('div');
          crow.className = 'constraint-row';
          const label = document.createElement('span');
          label.textContent = constraintLabel(c);
          label.title = label.textContent;
          const del = document.createElement('button');
          del.className = 'chat-mini danger';
          del.textContent = '✕';
          del.addEventListener('click', () => {
            const next = constraints.filter((_c, k) => k !== i);
            void app.updateFeatureParams(feature.id, {
              constraints: next.length > 0 ? next : undefined,
            });
          });
          crow.append(label, del);
          panel.appendChild(crow);
        });
      }
    }

    // Status line
    const status = app.doc.lastReport?.statuses.get(feature.id);
    if (status?.state === 'error') {
      const s = document.createElement('div');
      s.className = 'status-error';
      s.textContent = `⚠ ${status.message}`;
      panel.appendChild(s);
    } else if (status?.state === 'blocked') {
      const s = document.createElement('div');
      s.className = 'status-blocked';
      s.textContent = '⚠ blocked by an upstream error';
      panel.appendChild(s);
    }

    // Actions
    const actions = document.createElement('div');
    actions.className = 'actions';
    const mkBtn = (label: string, onClick: () => void, danger = false): void => {
      const b = document.createElement('button');
      b.textContent = label;
      if (danger) b.className = 'danger';
      b.addEventListener('click', onClick);
      actions.appendChild(b);
    };
    if (feature.type === 'sketch') mkBtn('Edit', () => app.editSketch(feature.id));
    mkBtn(feature.suppressed ? 'Unsuppress' : 'Suppress', () =>
      void app.setSuppressed(feature.id, !feature.suppressed),
    );
    mkBtn('Delete', () => void app.deleteFeature(feature.id), true);
    panel.appendChild(actions);
  };

  app.onUI(render);
  render();
}
